import api from '../../lib/api';
import type { SearchOption } from '../components/SearchSelect';

/** Stable fetchers for SearchSelect pickers (module scope — see SearchSelect docs). */

export const searchActiveUsers = async (term: string): Promise<SearchOption[]> => {
  const response = await api.get('/admin/users', {
    params: { search: term, limit: 10, status: 'active' },
  });
  const users = (response.data.users ?? []) as Array<{
    id: string;
    displayName: string | null;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    email: string | null;
  }>;
  return users.map((user) => ({
    id: user.id,
    label:
      user.displayName ||
      [user.firstName, user.lastName].filter(Boolean).join(' ') ||
      user.username ||
      user.email ||
      user.id,
    sublabel: user.email ?? undefined,
  }));
};

export const searchActiveOrganizations = async (term: string): Promise<SearchOption[]> => {
  const response = await api.get('/admin/accounts/organizations', {
    params: { search: term || undefined, status: 'active', limit: 100 },
  });
  const organizations = (response.data.data.organizations ?? []) as Array<{
    id: string;
    name: string;
  }>;
  return organizations.map((org) => ({ id: org.id, label: org.name }));
};

/**
 * Endorsing organizations: Capitol Commons orgs, registered charities and SOS
 * business entities. Ids are typed refs (org:/charity:/business:) that the
 * endorsement writes resolve as organization_ref — never send them as organization_id.
 */
export const searchEndorserOrganizations = async (term: string): Promise<SearchOption[]> => {
  const response = await api.get('/admin/endorsements/endorser-organizations', {
    params: { search: term || undefined },
  });
  return (response.data.data.options ?? []) as SearchOption[];
};

export const searchActiveWorkspaces = async (term: string): Promise<SearchOption[]> => {
  const response = await api.get('/admin/workspaces', {
    params: { search: term, limit: 10, status: 'active' },
  });
  const workspaces = (response.data.workspaces ?? []) as Array<{
    id: string;
    name: string;
    organization_name: string | null;
  }>;
  return workspaces.map((workspace) => ({
    id: workspace.id,
    label: workspace.name,
    sublabel: workspace.organization_name ?? undefined,
  }));
};

/**
 * Committees in the campaign finance import, for linking a committee to a ballot
 * measure (Campaign Committee Registry PRD §5.4). Every option is a TRACER committee
 * (`co:<co_id>`); committees that haven't filed aren't in the import, so they can't
 * be offered. Searches current and former names, active or not.
 */
export const searchImportedCommittees = async (term: string): Promise<SearchOption[]> => {
  const response = await api.get('/admin/committees', {
    params: { search: term || undefined, active: 'false', limit: 15 },
  });
  const committees = (response.data.data ?? []) as Array<{
    co_id: string;
    name: string | null;
    committee_type: string | null;
    last_seen: string | null;
    former_name_match: string | null;
  }>;
  return committees.map((committee) => ({
    id: `co:${committee.co_id}`,
    label: committee.name || committee.co_id,
    sublabel: [
      committee.former_name_match ? `formerly ${committee.former_name_match}` : null,
      `TRACER ${committee.co_id}`,
      committee.committee_type,
      committee.last_seen ? `last filed ${String(committee.last_seen).slice(0, 10)}` : null,
    ].filter(Boolean).join(' · '),
  }));
};

/** The TRACER committee id behind a picker selection, when it has one. */
export const financeCoIdOf = (option: SearchOption | null): string | null =>
  option?.id.startsWith('co:') ? option.id.slice(3) : null;

export const searchPersons = async (term: string): Promise<SearchOption[]> => {
  const response = await api.get('/persons/search', {
    params: { q: term || undefined, limit: 10 },
  });
  const persons = (response.data.data?.persons ?? []) as Array<{
    id: string;
    full_name: string | null;
    primary_email: string | null;
  }>;
  return persons.map((person) => ({
    id: person.id,
    label: person.full_name || person.id,
    sublabel: person.primary_email ?? undefined,
  }));
};
